import { useMediaQuery as useChakraMQ } from '@chakra-ui/react'

// https://chakra-ui.com/docs/hooks/use-media-query
// ! keep in mind this API relies on the users browser support of window.matchMedia and will always return false if it is not supported or does not exist (e.g. during SS rendering).

export type TMQOptions = {
  breakpointPx: number
}

/**
 * MQ hook accepting any valid CSS MQ as a string
 * and returning boolean result based on whether that MQ is fulfilled or not
 * usage: `const isPrinted = useMediaQuery('print')`
 */
export const useMediaQuery = (query: string) => {
  const [result] = useChakraMQ(`(${query})`)
  return result
}

/**
 * MQ hook accepting multiple valid CSS MQs as a string array
 * and returning boolean values based on whether that MQs are fulfilled or not
 * usage: `const [isPrinted, reduceMotion] = useMediaQuery(['print', 'prefers-reduced-motion'])`
 */
export const useMediaQueries = (queries: string[]) => {
  const results = useChakraMQ(queries.map((query) => `(${query})`))
  return results
}

/**
 * MQ hook for the most common MQ case – viewport width
 * returns both sides booleans for more convenient usage
 */
export const useWidthBreakpointPx = ({ breakpointPx }: TMQOptions) => {
  const isLarger = useMediaQuery(`min-width: ${breakpointPx}px`)
  return { isLarger, isSmaller: !isLarger }
}

/**
 * MQ hook for the most common MQ case – viewport width
 * returns just the "isLarger" part
 */
export const useWiderThanPx = ({ breakpointPx }: TMQOptions) => {
  return useMediaQuery(`min-width: ${breakpointPx}px`)
}

/**
 * MQ hook for the most common MQ case – viewport width
 * returns just the "isSmaller" part
 */
export const useThinnerThanPx = ({ breakpointPx }: TMQOptions) => {
  return !useWiderThanPx({ breakpointPx })
}

/**
 * MQ hook for viewport height
 * returns just the "isLarger" part
 */
export const useTallerThanPx = ({ breakpointPx }: TMQOptions) => {
  return useMediaQuery(`min-height: ${breakpointPx}px`)
}

/**
 * MQ hook for viewport height
 * returns just the "isLarger" part
 */
export const useShorterThanPx = ({ breakpointPx }: TMQOptions) => {
  return !useTallerThanPx({ breakpointPx })
}
